import React from "react";
import { Box } from "@chakra-ui/react";
import DestinationMarquee from "components/destination-marquee";
import { HalfHero } from "components/half-hero";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Section, sectionSpacingY } from "components/section";
import { SectionHeading } from "components/section-heading";
import {
  HeroGradient,
  AudienceActivationAnimation,
  ActivationFeatures,
  MatchBoosterOverviewAnimation,
  MatchBoosterFeatures,
  OrchestrationFeatures,
  PrivacyComplianceFeatures,
  CampaignIntelligenceFeatures,
  CustomerStudioPreview,
  CampaignIntelligencePreview,
  HeroGradientMobile,
} from "components/solutions/advertising";
import { StackedLogos } from "components/logos";
import {
  getAnnouncementBanner,
  getFeaturedCustomers,
  getPage,
  getSimpleIntegrations,
  getStackedLogos,
  type Banner,
  type Customer,
  type IntegrationSimple,
  type Page as SanityPage,
} from "lib/sanity";
import { type GetStaticProps, type NextPage } from "next";
import { getPageMetaImage } from "utils";
import { FullWidthAnimationCard, FullWidthFeatureCard } from "components/cards";
import { CustomerStats, SecurityFeatures } from "components/content";
import { ArrowLink } from "ui/link";
import heroImage from "../../../public/assets/solutions/advertising/hero.png";
import activationDiagramMobile from "../../../public/assets/solutions/advertising/activation-diagram-mobile.png";
import matchBoosterDiagramMobile from "../../../public/assets/solutions/advertising/match-booster-diagram-mobile.png";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
  customerRows: Customer[][];
  featuredStatCustomers: Customer[];
};

const Advertising: NextPage<PageProps> = ({
  page,
  destinations,
  announcementBanner,
  customerRows,
  featuredStatCustomers,
}) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Box pos="relative">
        <HeroGradient />

        <HalfHero
          hasBackgroundGradient
          hideSecondary
          eyebrow={page.eyebrow}
          title={page.title}
          description={page.description}
          gap={{ base: 12, md: 16, lg: 8, xl: 12 }}
          image={heroImage}
          imageRatio={8 / 7}
          imageObjectFit="contain"
          backgroundGradient={<HeroGradientMobile />}
        />

        <StackedLogos title="" customerRows={customerRows} my={{ base: 16, lg: 20 }} />
      </Box>

      <CustomerStats customers={featuredStatCustomers} />

      <Section center>
        <SectionHeading
          eyebrow="Activation"
          title="Activate audiences & conversions across 40+ media channels"
          description="Stop waiting on technical teams and slow software to launch targeted campaigns. Sync live audiences and conversion events across your media ecosystem instantly, with the click of a button."
          maxW="4xl"
          mx="auto"
        />

        <FullWidthAnimationCard
          variant="green"
          animationElement={<AudienceActivationAnimation />}
          mobileImage={activationDiagramMobile}
          imageAlt="A diagram demonstrating audience activation using Hightouch."
          py={{ base: 8, sm: 12 }}
        />

        <ActivationFeatures />
      </Section>

      <Section>
        <CenteredQuote
          maxW="3xl"
          quoteText="Now, with Hightouch, we can unlock fresh new campaign ideas in just a few clicks."
          author={{
            name: "Kishan Antala",
            job: "Senior Product Manager",
          }}
          company={{
            name: "Morning Brew",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/563e0f725e996b98a52c6b757dea1fc14c59e5d0-1014x179.png",
            logoRatio: 5 / 1,
          }}
        />
      </Section>

      <Section center mt={{ base: "biggest", md: "huge" }}>
        <FullWidthFeatureCard
          variant="blue"
          eyebrow="Orchestration"
          title="Orchestrate always-on campaigns in a few clicks"
          description="Hightouch provides sophisticated no-code tools for targeting, suppression, and conversion optimization so that you can focus less on advertising operations, and more on launching the next great campaign."
          cta={{ href: "/platform/customer-studio", text: "Explore Customer Studio" }}
          featureNode={<CustomerStudioPreview />}
        />

        <OrchestrationFeatures />
      </Section>

      <Section center>
        <SectionHeading
          eyebrow="Cookieless Identity Solution"
          title="Reach and convert more customers in a cookieless world"
          description="Engage more customers across more screens with a cookieless identity graph that maximizes audience reach, enhances conversion quality, and improves campaign performance."
          maxW="4xl"
          mx="auto"
        />

        <ArrowLink variant="grass" href="/platform/match-booster" mt={{ base: 6, md: 8 }} size="lg">
          Explore Match Booster
        </ArrowLink>

        <FullWidthAnimationCard
          variant="green"
          animationElement={<MatchBoosterOverviewAnimation />}
          mobileImage={matchBoosterDiagramMobile}
          imageAlt="A diagram demonstrating Hightouch's Match Booster features."
        />

        <MatchBoosterFeatures />
      </Section>

      <Section>
        <CenteredQuote
          maxW="2xl"
          quoteText="It's like pushing the easy button for improving ad performance."
          author={{
            name: "Adam Ribaudo",
            job: "VP of Data Activation",
          }}
          company={{
            name: "Velir",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/44a86c9b839dc1822f530190d889022e13f66c1a-359x100.svg",
            logoRatio: 5 / 1,
          }}
        />
      </Section>

      <Section center my={0} pb={sectionSpacingY}>
        <FullWidthFeatureCard
          variant="blue"
          eyebrow="Campaign Intelligence"
          title="Measure everything in one place"
          description="Bring all of your campaign data and business metrics into one place (your data warehouse) for easy and accurate omni-channel measurement. Use our suite of self-service tools to analyze campaign performance,  measure incremental lift, and understand ROI."
          cta={{ href: "/platform/campaign-intelligence", text: "Explore Campaign Intelligence" }}
          featureNode={<CampaignIntelligencePreview />}
        />

        <CampaignIntelligenceFeatures />
      </Section>

      <Section center bg="forest" color="white">
        <SectionHeading
          title="Privacy & compliance by default"
          description="Hightouch doesn’t store your data. Instead we simply read from your data warehouse where it stays safe and sound."
          maxW="4xl"
          mx="auto"
        />

        <PrivacyComplianceFeatures />

        <SecurityFeatures theme="dark" title="" description="" my="0" />
      </Section>

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export default Advertising;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, destinations, customerRows, featuredStatCustomers] = await Promise.all([
    getPage("/solutions/advertising"),
    getAnnouncementBanner(),
    getSimpleIntegrations("destination"),
    getStackedLogos(),
    getFeaturedCustomers(["the-zebra", "lucidchart", "gorgias"]),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      destinations,
      customerRows,
      featuredStatCustomers,
    },
    revalidate: 60,
  };
};
